import axios from 'axios'
import { useCallback, useEffect, useState } from 'react'
import { useToaster } from '@enterprise-ui/canvas-ui-react'
import { getUserSessionToken } from '../../../store/userProfile'
import store from '../../../store'
import veItemBulkMfeConfig from '../../../config/'
import { BULK_UPDATE } from '../../../constants/bulkUtilitiesConstants'

export const useUpload = (handleUploadChange, source = 'bulk_upload_LP') => {
  const makeToast = useToaster()
  const [files, setFiles] = useState([])
  const [error, setError] = useState(null)
  const [uploadError, setUploadError] = useState(null)
  const [uploadingFile, setUploadingFile] = useState(false)
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [pendingFile, setPendingFile] = useState(null)

  useEffect(() => {
    if (uploadError) {
      makeToast({
        type: 'error',
        heading: 'File Upload Failed',
        message: uploadError,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadError])

  const handleFileUpload = useCallback(
    async (file) => {
      const sessionToken = getUserSessionToken(store.getState())
      const uploadUrl = veItemBulkMfeConfig.bulk.url + 'upload'
      const formData = new FormData()
      formData.append('file', file)

      try {
        setUploadingFile(true)
        const header = {
          'Content-Type': 'multipart/form-data', // May be set automatically by the browser
          'x-api-key': veItemBulkMfeConfig.apiKey,
          User_id: sessionToken,
          source: source,
        }
        await axios
          .post(uploadUrl, formData, {
            headers: header,
          })
          .then(() => {
            makeToast({
              type: 'success',
              heading: 'File Upload Submitted',
              message: `Your file '${file.name}' has been submitted. Processing time varies by file size. Check ‘Upload History’ to track progress.`,
            })
          })
        return []
      } catch (error) {
        if (axios.isCancel(error)) {
          setUploadError(
            `Your file '${file.name}' upload was interrupted due to network issue. Please try again.`,
          )
        } else if (error.response) {
          // Server responded with a status
          switch (error.response.status) {
            case 400:
              setUploadError(`${error.response?.data}`)
              break
            case 405:
              setUploadError('Upload Method failed. Please try again.')
              break
            case 413:
              setUploadError(
                'File size too large. Please upload a smaller file.',
              )
              break
            default:
              setUploadError(
                `Unexpected server error: ${error.response.status}`,
              )
              break
          }
        } else if (error.code === 'ECONNABORTED') {
          // Axios timeout error
          setUploadError(
            `Upload time out. Please check your connection and try again.`,
          )
        } else {
          // Something else, likely network or browser interruption
          setUploadError('Upload interrupted. Check your internet connection.')
        }
        return []
      } finally {
        setUploadingFile(false)
        handleUploadChange()
      }
    },
    [handleUploadChange, makeToast, source],
  )
  const handleXlFiles = useCallback(
    async (files) => {
      setError(null)
      try {
        for (const file of files) {
          const uploadResult = await handleFileUpload(file)
          if (uploadResult) {
            setFiles((prevFiles) => [...prevFiles, ...uploadResult])
          }
        }
      } catch (error) {
        setError(error.message)
      }
    },
    [handleFileUpload],
  )

  const onUpdate = (e) => {
    const uploadedFile =
      e?.type === 'drop' ? e.dataTransfer?.files?.[0] : e.target?.files?.[0]
    if (!uploadedFile) return
    setPendingFile(uploadedFile)
    setShowConfirmationModal(true)

    // Reset the input value to allow uploading the same file again if needed
    if (e.target) {
      e.target.value = null
    }
  }

  const confirmAndUpload = async () => {
    const uploadedFile = pendingFile
    setShowConfirmationModal(false)

    if (!uploadedFile) return

    const maxFileSize = BULK_UPDATE.UPLOAD_SPREADSHEET.FILESIZE

    if (uploadedFile) {
      if (
        BULK_UPDATE.UPLOAD_SPREADSHEET.ALLOWED_TYPES.includes(uploadedFile.type)
      ) {
        if (uploadedFile.size > maxFileSize) {
          makeToast({
            type: 'alert',
            heading: 'Limit exceeded!',
            message: `File is too large. Max size is ${maxFileSize / 1000000} MB.`,
          })
          setUploadingFile(false)
          return
        } else {
          // Start upload
          setUploadingFile(true)
          try {
            await handleXlFiles([uploadedFile])
            handleUploadChange?.()
          } finally {
            setUploadingFile(false)
            setPendingFile(null)
          }
        }
      } else {
        makeToast({
          type: 'error',
          heading: 'Invalid file type',
          message: BULK_UPDATE.UPLOAD_SPREADSHEET.FILE_TYPE_ERROR,
        })
        setUploadingFile(false)
        return
      }
    }
  }

  const cancelUpload = () => {
    setShowConfirmationModal(false)
    setPendingFile(null)
  }

  return {
    onUpdate,
    files,
    error,
    uploadingFile,
    showConfirmationModal,
    confirmAndUpload,
    cancelUpload,
    pendingFile,
  }
}
